import { useEffect, useRef, useState, useCallback } from "react";
import * as React from "react";

// CSS TEST
/* eslint import/no-webpack-loader-syntax: off */
// @ts-ignore
import css from "!raw-loader!./exp.css";

//QUERIES
//

//WEBGL
//
import { Canvas } from "@react-three/fiber";
import * as THREE from "three";

//ICONS
//
import { Hotspot, Scene, TourData } from "../../../Types";
import ExpHotspot from "./Hotspots/ExpHotspot";
import { UseAssetsQuery, UseGetTour } from "../../../api/calls/Queries.ts";

import * as BottomOverlay from "./BottomOverlay.tsx";
import { useQueryClient } from "@tanstack/react-query";
import {
  editorExperienceStore,
  useCurrentSceneStore,
  useParametersStore,
  useSceneStore,
  useWidePopupStore,
} from "../../../api/Zustand.ts";
import Overlay from "./Overlay.tsx";
import LoadingExp from "../../common/LoadingExp.tsx";
import WebGLSphere from "../../common/WebGLSphere.tsx";
import Spot from "./Spot.tsx";
import Controls from "../../common/Controls.tsx";

import ScreenshotRectangle from "./ScreenshotRectangle.tsx";
import { useGLRendererStore } from "../../../state management/zustand/gl-stores.ts";
import Camera from "../../common/Camera.tsx";
import { getLanguageFile } from "../../common/GetLanguageFile.ts";

function Experience() {
  const { tourID } = useParametersStore();

  const { data: tourData } = UseGetTour(tourID);

  const { currentSceneID } = useCurrentSceneStore();

  const { transition } = editorExperienceStore();

  // const handle = useFullScreenHandle();

  const el = useRef<HTMLDivElement>(null);

  const currentScene = React.useMemo(() => {
    return tourData?.scenes.find((one) => one.id === currentSceneID);
  }, [tourData?.scenes, currentSceneID]);

  return (
    // <FullScreen handle={handle}>
    <div className="experience" ref={el}>
      <style>{css}</style>
      <ScreenshotRectangle el={el} />
      <ExpHotspot />

      <div className="black-transition" id={transition ? undefined : "hidden"}>
        {currentScene?.imageID || currentScene?.imageFull ? (
          <LoadingExp />
        ) : tourData?.scenes.length === 0 ? (
          getLanguageFile("experience.empty_tour")
        ) : (
          getLanguageFile("experience.upload_pano")
        )}
      </div>

      <R3F tourData={tourData!} tourID={tourID} />
    </div>
    // </FullScreen>
  );
}
export default Experience;

const R3F = (props: { tourData: TourData; tourID: number }) => {
  const { tourData, tourID } = props;

  const { currentSceneID } = useCurrentSceneStore();

  const { scene, setScene } = useSceneStore();

  useEffect(() => {
    if (currentSceneID)
      setScene(
        tourData?.scenes.find((one: Scene) => one.id === currentSceneID)
      );
  }, [tourData?.scenes, currentSceneID, setScene]);

  //
  //ZOOM HANDLING
  //

  const [zoom, setZoom] = useState<number>(
    75 || tourData?.scenes.find((one: Scene) => one.id === currentSceneID)?.fov
  );

  const zoomHandling = useCallback(
    (e: React.WheelEvent) => {
      if (Math.sign(e.deltaY) === -1) {
        if (zoom <= 15) return;
        else setZoom(zoom - 4);
      } else {
        if (zoom >= 90) return;
        else setZoom(zoom + 4);
      }
    },
    [zoom]
  );

  //
  //COMPASS ICON
  //

  const { setState } = useGLRendererStore();

  return (
    <>
      <Canvas
        gl={{ preserveDrawingBuffer: true }}
        onCreated={(gl) => {
          console.debug(gl);
          setState(gl);
        }}
        onWheel={zoomHandling}
        flat
        linear
        dpr={1}
      >
        <color args={["gray"]} attach={"background"}></color>
        <Camera scene={scene!} zoom={zoom}></Camera>

        {scene && <Controls scene={scene} zoom={zoom} />}

        {/* {scene && <Sphere scene={scene} tourData={tourData} />} */}
        {scene && <WebGLSphere scene={scene} tourData={tourData} />}

        {scene?.hotspots && (
          <Hotspots
            hotspots={scene.hotspots}
            tourData={tourData}
            tourID={tourID}
          />
        )}
      </Canvas>

      <Overlay tourData={tourData} />

      {scene?.hotspots?.some((spot: Hotspot) => spot.EDIT_POS === true) ? (
        <>
          <div className="border-overlay" />
          <BottomOverlay.TransformationInfo />
        </>
      ) : (
        <BottomOverlay.Warnings scene={scene!} tourData={tourData} />
      )}
    </>
  );
};

const Hotspots = (props: {
  hotspots: Hotspot[];
  tourData: TourData;
  tourID: number;
}) => {
  const { hotspots, tourData, tourID } = props;

  const { data: AudioList } = UseAssetsQuery(tourID, 3);
  const { isTakingScreenshot } = useWidePopupStore();

  const queryClient = useQueryClient();
  const { currentSceneID } = useCurrentSceneStore();

  const onDragStart = React.useCallback(() => {
    queryClient.setQueryData(["GetTour", tourID], {
      ...tourData,
      scenes: [
        ...tourData?.scenes.map((sc) =>
          sc.id === currentSceneID
            ? {
                ...sc,
                hotspots: sc.hotspots.map((hs) =>
                  hs.EDIT_POS === true
                    ? {
                        ...hs,
                        DRAGGING: true,
                      }
                    : hs
                ),
              }
            : sc
        ),
      ],
    });
  }, [queryClient, tourID, tourData, currentSceneID]);

  const handlePosChange = React.useCallback(
    async (pos: THREE.Vector3) => {
      return await queryClient.setQueryData(["GetTour", tourID], {
        ...tourData,
        scenes: [
          ...tourData?.scenes.map((sc) =>
            sc.id === currentSceneID
              ? {
                  ...sc,
                  hotspots: sc.hotspots.map((hs) =>
                    hs.EDIT_POS === true
                      ? {
                          ...hs,
                          x: pos.x,
                          y: pos.y,
                          z: pos.z,
                          pos: pos,
                          DRAGGING: null,
                        }
                      : hs
                  ),
                }
              : sc
          ),
        ],
      });
    },

    [queryClient, tourData, currentSceneID, tourID]
  );

  if (!isTakingScreenshot)
    return (
      <>
        {hotspots.map((spot) => (
          <Spot
            hotspots={hotspots}
            key={spot.id}
            AudioList={AudioList}
            spot={spot}
            tourData={tourData}
            handlePosChange={handlePosChange}
            onDragStart={onDragStart}
          />
        ))}
      </>
    );
};
